import { useState, useEffect } from 'react';

import { useModal } from '@procurify/ui'

import {
  type CreateSavedFilterSchema,
  SavedFilterAPI,
} from '@webapp/api/SavedFilterAPI'
import { type FilterManagerContextValues } from '@webapp/components/FilterManager/FilterManagerContext'
import { type FilterManagerFilter } from '@webapp/components/FilterManager/types'
import { toast } from '@webapp/components/Notification'
import { type ISavedFiltersFormProps } from '@webapp/components/SavedFiltersForm/SavedFiltersForm'
import { SavedFiltersFormModal } from '@webapp/components/SavedFiltersForm/SavedFiltersFormModal'
import { type SavedFilter } from '@webapp/models/SavedFilter'
import messages from './messages'
import {
  type SavedFilterCollection,
  type SavedFilterCollectionSchemas,
} from './types'
import { useProcurifyIntl } from '../useProcurifyIntl'

export interface UseSavedFilterManagerOptions<
  CollectionId extends SavedFilterCollection,
  Filters extends { [key: string]: any },
  CollectionSchema extends SavedFilterCollectionSchemas[CollectionId],
> {
  collectionId: CollectionId
  filterManager: FilterManagerContextValues<Filters>
  /**
   * Objects of functions to decode saved filter shape
   * into the expected form control's shape
   */
  decodeFilters: {
    [key in keyof Filters & keyof CollectionSchema]: (
      savedValue: CollectionSchema[key],
      params: Partial<SavedFilterCollectionSchemas[CollectionId]>
    ) => FilterManagerFilter<Filters[key]>
  }
  /**
   * Objects of functions to encode form control's shape
   * into the expected saved filter shape
   */
  encodeFilters: {
    [key in keyof Filters & keyof CollectionSchema]: (
      filterValue: FilterManagerFilter<Filters[key]>['value']
    ) => CollectionSchema[key]
  }
  /**
   * An array of keys that are allowed to be saved. All keys
   * are allowed if this is undefined.
   *
   * If a disallowed key is in the filters, then `hasValidFilters` will be `false`
   */
  allowedFilters?: (keyof Filters)[]
}

export interface UseSavedFilterManager<
  CollectionId extends SavedFilterCollection,
  Filters extends { [key: string]: any },
  CollectionSchema extends SavedFilterCollectionSchemas[CollectionId],
> {
  collectionId: SavedFilterCollection
  hasValidFilters: boolean
  hasFilters: boolean
  selectedSavedFilter: SavedFilter
  setSelectedSavedFilter: (savedFilter: SavedFilter) => void
  /**
   * Opens the saved filter modal to let the user save
   * the current selected filters
   */
  saveFilters: () => void
  /**
   * Apply the selected saved filters into the filter manager
   */
  applySavedFilters: (
    savedFilters: SavedFilter<CollectionSchema>['filters']
  ) => void
  filterManager: FilterManagerContextValues<Filters>
}

export function useSavedFilterManager<
  CollectionId extends SavedFilterCollection,
  Filters extends { [key: string]: any },
  CollectionSchema extends SavedFilterCollectionSchemas[CollectionId],
>({
  filterManager,
  collectionId,
  decodeFilters,
  encodeFilters,
  allowedFilters,
}: UseSavedFilterManagerOptions<
  CollectionId,
  Filters,
  CollectionSchema
>): UseSavedFilterManager<CollectionId, Filters, CollectionSchema> {
  const modal = useModal()
  const intl = useProcurifyIntl()

  const [selectedSavedFilter, setSelectedSavedFilter] =
    useState<SavedFilter>(null)

  const { filterValues, filterTags, clearFilters, addFilter } = filterManager

  const isValid = Array.isArray(allowedFilters)
    ? !Object.keys(filterValues).some(
        (filterKey) => allowedFilters.indexOf(filterKey) < 0
      )
    : true

  const hasFilters = Object.values(filterValues).some((val) => !!val)

  useEffect(() => {
    if (!hasFilters) {
      setSelectedSavedFilter(null)
    }
  }, [hasFilters])

  const onSubmitSaveFilters: ISavedFiltersFormProps['onSubmit'] = (
    value,
    formikActions
  ) => {
    formikActions.setSubmitting(true)

    const data: CreateSavedFilterSchema = {
      ...value,
      filters: {},
    }

    data.filters = Object.keys(filterValues).reduce((prev, filterKey) => {
      if (encodeFilters[filterKey]) {
        prev[filterKey] = encodeFilters[filterKey](filterValues[filterKey])
      } else {
        prev[filterKey] = filterValues[filterKey]
      }
      return prev
    }, {})

    SavedFilterAPI.saveFilters(data)
      .then((res) => res.data)
      .then((newSavedFilter) => {
        formikActions.setSubmitting(false)

        setSelectedSavedFilter(newSavedFilter)

        modal.hideModal()

        toast.success({
          title: intl.formatMessage(messages.notificationSuccessTitle),
          children: intl.formatMessage(messages.notificationSaveSuccessMessage),
        })
      })
      .catch(() => {
        formikActions.setSubmitting(false)
      })
  }

  const saveFilters: UseSavedFilterManager<
    CollectionId,
    Filters,
    CollectionSchema
  >['saveFilters'] = () => {
    modal.showModal(SavedFiltersFormModal, {
      open: true,
      collection: collectionId,
      filters: filterTags,
      onSubmit: onSubmitSaveFilters,
    })
  }

  const applySavedFilters: UseSavedFilterManager<
    CollectionId,
    Filters,
    CollectionSchema
  >['applySavedFilters'] = (savedFilters) => {
    clearFilters()

    Object.keys(savedFilters).forEach((savedFilterKey) => {
      const decodeFilterFn = decodeFilters[savedFilterKey]
      const savedFilterData = savedFilters[savedFilterKey]

      if (
        decodeFilterFn &&
        savedFilterData !== undefined &&
        savedFilterData !== null
      )
        addFilter(decodeFilterFn(savedFilterData, savedFilters))
    })
  }

  return {
    collectionId,
    hasValidFilters: isValid,
    selectedSavedFilter,
    setSelectedSavedFilter,
    hasFilters,
    saveFilters,
    applySavedFilters,
    filterManager,
  }
}
